import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import "mapbox-gl/dist/mapbox-gl.css";
import { geofenceFeature, validPosition, locationsForMatches } from "./geofences";
import { useNavigate } from "react-router";

/** Draws current boundaries and an optional saved GPS point in longitude/latitude order. */
export function GeofenceMap({ locations, mapBoxApiKey, position, selectedId, height = 400 }) {
    const container = useRef(null);
    const [error, setError] = useState("");
    const [mapStyle, setMapStyle] = useState(() =>
        localStorage.getItem("selectedMapStyle") || "mapbox://styles/mapbox/standard");
    useEffect(() => {
        if (!mapBoxApiKey) return;
        let map;
        setError("");
        try {
            mapboxgl.accessToken = mapBoxApiKey;
            map = new mapboxgl.Map({
                container: container.current,
                style: mapStyle,
                center: [-2, 54], zoom: 5,
            });
            map.addControl(new mapboxgl.NavigationControl());
            map.addControl(new mapboxgl.ScaleControl({ unit: "metric" }));
            map.on("error", () => setError("Map could not load."));
            map.on("load", () => {
                const features = locations.map(geofenceFeature).filter(Boolean);
                map.addSource("geofences", { type: "geojson", data: { type: "FeatureCollection", features } });
                const color = ["case", ["get", "enabled"], "#36b37e", "#8c939b"];
                map.addLayer({ id: "geofence-fill", type: "fill", source: "geofences",
                    paint: { "fill-color": color, "fill-opacity": 0.17 } });
                map.addLayer({ id: "geofence-line", type: "line", source: "geofences",
                    paint: { "line-color": color, "line-width": 2 } });
                const bounds = new mapboxgl.LngLatBounds();
                const selected = features.find(f => String(f.properties.id) === String(selectedId));
                (selected ? [selected] : features).forEach(f => {
                    f.geometry.coordinates.flat(f.geometry.type === "Polygon" ? 1 : 2)
                        .forEach(point => bounds.extend(point));
                });
                if (position && validPosition(position[0], position[1])) {
                    new mapboxgl.Marker({ color: "#ffb547" }).setLngLat(position).addTo(map);
                    if (!selected) bounds.extend(position);
                }
                if (!bounds.isEmpty()) map.fitBounds(bounds, { padding: 50, maxZoom: 17, duration: 0 });
            });
        } catch { setError("Map unavailable."); }
        return () => map?.remove();
    }, [locations, mapBoxApiKey, position, selectedId, mapStyle]);
    return <>
        <div className="d-flex align-items-center justify-content-end mb-2">
            <label htmlFor="waylens-map-style" className="mb-0 mr-2">Map style</label>
            <select id="waylens-map-style" className="form-control bg-dark text-light" style={{ width: "auto" }}
                value={mapStyle} onChange={event => {
                    setMapStyle(event.target.value);
                    localStorage.setItem("selectedMapStyle", event.target.value);
                }}>
                <option value="mapbox://styles/mapbox/standard">Standard</option>
                <option value="mapbox://styles/mapbox/satellite-streets-v12">Satellite</option>
                <option value="mapbox://styles/mapbox/dark-v11">Dark</option>
            </select>
        </div>
        {!mapBoxApiKey && <div className="text-muted">Map configuration unavailable.</div>}
        {error && <div className="alert alert-warning">{error}</div>}
        <div ref={container} style={{ height, width: "100%", background: "#101820" }} />
    </>;
}

/** Read-only catalogue, or a map of only the event's saved matches when embedded in Map View. */
export default function WaylensGeofences({ apiFetch, mapBoxApiKey, position, embedded = false, matches }) {
    const navigate = useNavigate();
    const [locations, setLocations] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const response = await apiFetch("/api/Waylens/geofences");
                if (!response.ok) throw new Error("Unable to load geofence locations.");
                const data = await response.json();
                if (active) setLocations(data);
            } catch (e) { if (active) setError(e.message); }
            finally { if (active) setLoading(false); }
        })();
        return () => { active = false; };
    }, [apiFetch]);
    const visibleLocations = embedded ? locationsForMatches(locations, matches) : locations;
    return <div className={embedded ? "" : "webcontainer p-3 text-light"}>
        {!embedded && <div className="d-flex align-items-center mb-3">
            <button type="button" className="btn btn-outline-light mr-3" onClick={() => navigate("/waylens")}>Back</button>
            <h4 className="mb-0">Waylens Geofence Locations</h4>
        </div>}
        {!embedded && <p className="text-muted">Current boundaries · Green: enabled · Grey: disabled</p>}
        {error && <div className="alert alert-danger">{error}</div>}
        {loading ? <p>Loading locations…</p> : <>
            {embedded && visibleLocations.length > 0 && <small className="text-muted">Current boundary</small>}
            {embedded && matches?.some(match => !visibleLocations.some(location =>
                String(location.id) === String(match.geofenceId) && geofenceFeature(location)))
                && <small className="d-block text-muted mb-2">Some saved locations no longer have an available boundary.</small>}
            <GeofenceMap locations={visibleLocations} mapBoxApiKey={mapBoxApiKey} position={position} selectedId={selectedId}
                height={embedded ? "clamp(360px, calc(100vh - 290px), 850px)" : 400} />
            {!embedded && <div className="table-responsive">
                <table className="table table-dark mb-0">
                    <thead><tr><th>Location</th><th>State</th><th>Shape</th><th>Radius</th><th>Version</th></tr></thead>
                    <tbody>{locations.map(location => <tr key={location.id}>
                        <td><button type="button" className="btn btn-link p-0" onClick={() => setSelectedId(location.id)}>
                            {location.label}</button>{!geofenceFeature(location) && <small> · Boundary unavailable</small>}</td>
                        <td>{location.isEnabled ? "Enabled" : "Disabled"}</td>
                        <td>{location.shapeType === 0 || location.shapeType === "Circle" ? "Circle" : "Polygon"}</td>
                        <td>{location.shapeType === 0 || location.shapeType === "Circle" ? `${location.radiusMetres ?? "-"} m` : "—"}</td>
                        <td>{location.version}</td>
                    </tr>)}</tbody>
                </table>
            </div>}
            {!embedded && !locations.length && <p>No configured geofences.</p>}
        </>}
    </div>;
}
